import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const skip = () =>
  typeof window === "undefined" ||
  navigator.webdriver ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
  !window.matchMedia("(pointer: fine)").matches; // touch devices keep native UX

/**
 * Custom cursor: a small dot that sticks to the pointer and a trailing ring
 * that swells over interactive elements (links, buttons, [data-cursor]).
 * Desktop-only; the native cursor stays visible underneath.
 */
const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (skip()) return;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    gsap.set([dot, ring], { xPercent: -50, yPercent: -50, opacity: 0 });

    const dotX = gsap.quickTo(dot, "x", { duration: 0.12, ease: "power3.out" });
    const dotY = gsap.quickTo(dot, "y", { duration: 0.12, ease: "power3.out" });
    const ringX = gsap.quickTo(ring, "x", { duration: 0.45, ease: "power3.out" });
    const ringY = gsap.quickTo(ring, "y", { duration: 0.45, ease: "power3.out" });

    let visible = false;
    const onMove = (e) => {
      if (!visible) {
        visible = true;
        gsap.to([dot, ring], { opacity: 1, duration: 0.3 });
      }
      dotX(e.clientX);
      dotY(e.clientY);
      ringX(e.clientX);
      ringY(e.clientY);
    };

    const isInteractive = (el) =>
      el.closest?.('a, button, select, input, textarea, label, [data-cursor], [role="button"]');

    const onOver = (e) => {
      if (isInteractive(e.target)) {
        gsap.to(ring, { scale: 2.2, borderColor: "rgba(213,60,53,0.9)", duration: 0.3 });
        gsap.to(dot, { scale: 0.5, duration: 0.3 });
      }
    };
    const onOut = (e) => {
      if (isInteractive(e.target)) {
        gsap.to(ring, { scale: 1, borderColor: "rgba(255,255,255,0.5)", duration: 0.3 });
        gsap.to(dot, { scale: 1, duration: 0.3 });
      }
    };
    const onDown = () => gsap.to(ring, { scale: 0.8, duration: 0.15 });
    const onUp = () => gsap.to(ring, { scale: 1, duration: 0.25, ease: "back.out(2)" });
    const onLeave = () => {
      visible = false;
      gsap.to([dot, ring], { opacity: 0, duration: 0.3 });
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    document.documentElement.addEventListener("mouseleave", onLeave);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <>
      <div
        ref={dotRef}
        className="fixed top-0 left-0 w-2 h-2 rounded-full bg-reored pointer-events-none z-[120]"
        aria-hidden="true"
      />
      <div
        ref={ringRef}
        className="fixed top-0 left-0 w-9 h-9 rounded-full border border-white/50 pointer-events-none z-[110]"
        aria-hidden="true"
      />
    </>
  );
};

export default CustomCursor;
